import { useState, useEffect, useContext } from "react";
import { PermissionContext } from "../../contexts/PermissionContext";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { assignPermissionsToRole, assignPermissionsToUser } from "../../services/assignmentService";
import Notification from "../../components/UI/Notification";
import RoleSelector from "../Roles/RoleSelector";
import UserSelector from "../Users/UserSelector";

const AssignPermissions = () => {
  const { token } = useContext(AuthContext);
  const { permissions, loadPermissions } = useContext(PermissionContext); 
  const { roles, loadRoles } = useContext(RoleContext);
  const [target, setTarget] = useState("role"); 
  const [targetId, setTargetId] = useState("");
  const [formData, setFormData] = useState({ id: "" });
  const [assignedPermissions, setAssignedPermissions] = useState([]);
  const [removedPermissions, setRemovedPermissions] = useState([]);
  const [message, setMessage] = useState("");
  const [notification, setNotification] = useState(null);
  

  // Load permissions on component mount
  useEffect(() => {
    loadPermissions(token);
  }, [token, loadPermissions]);

  const handleAssignPermissions = async () => {
    setNotification(null);
    try {
      const data = {
        [target === "role" ? "role" : "user_id"]: formData.id, // Use formData.id
        permissions: assignedPermissions,
        remove: removedPermissions,
      };
      if (data.role === '') {
        setNotification({
          message: "Role name cannot be empty.",
          type: "error",
        });
        return;
      }

      if (data.user_id === '') {
        setNotification({
          message: "User name cannot be empty.",
          type: "error",
        });
        return;
      }

      if (data.permissions.length === 0 && data.remove.length === 0) {
        setNotification({
          message: "Provide permissions to assign or remove",
          type: "error",
        });
        return;
      }      

      const result =
        target === "role"
          ? await assignPermissionsToRole(token, data)
          : await assignPermissionsToUser(token, data);
      setNotification({
        message: `${result.message || "Permissions updated successfully."}`,
        type: "success",
      });
    } catch (err) {
      setNotification({
        message: `${err.message || "Failed to update permissions."}`,
        type: "error",
      });
    }
  };

  return (
    <div className="flex items-center justify-center font-poppins p-2">
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}
      <div className="w-full  dark:bg-gray-900 rounded p-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-4 text-center">
          Assign Permissions
        </h2>

        {message && (
          <p
            className={`text-sm font-medium mb-4 ${
              message.includes("successfully") ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}

        <div className="mb-4">
          <label htmlFor="target" className="block dark:text-white text-gray-700 font-medium mb-2">
            Assign To: <span className='text-red-500'>*</span>
          </label>
          <select
            id="target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="role">Role</option>
            <option value="user">User</option>
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="targetId" className="block dark:text-white text-gray-700 font-medium mb-2">
            {target === "role" ? "Role Name" : "User Name"}: <span className='text-red-500'>*</span>
          </label>
  
          {target === "role" ? (
            <RoleSelector formData={formData} setFormData={setFormData} />
          ) : (
            <UserSelector formData={formData} setFormData={setFormData} />
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="assignPermissions" className="block dark:text-white text-gray-700 font-medium mb-2">
            Assign Permissions: 
          </label>
          <select
            id="assignPermissions"
            multiple
            value={assignedPermissions}
            onChange={(e) =>
              setAssignedPermissions([...e.target.selectedOptions].map((o) => o.value))
            }
            className="w-full px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {permissions.map((permission) => (
              <option key={permission.id} value={permission.name}>
                {permission.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="removePermissions" className="block dark:text-white text-gray-700 font-medium mb-2">
            Remove Permissions:
          </label>
          <select
            id="removePermissions"
            multiple
            value={removedPermissions}
            onChange={(e) =>
              setRemovedPermissions([...e.target.selectedOptions].map((o) => o.value))
            }
            className="w-full px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {permissions.map((permission) => (
              <option key={permission.id} value={permission.name}>
                {permission.name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleAssignPermissions}
          className="float-right px-6 bg-blue-600 font-medium text-white py-2 rounded-md hover:bg-blue-700 transition duration-200"
        >
          Update Permissions
        </button>
      </div>
    </div>
  );
};

export default AssignPermissions;
